import {
	createCipheriv,
	createDecipheriv,
	randomBytes,
	scryptSync,
} from "crypto";

const ALGORITHM = "aes-256-gcm";
const SALT = (process.env.ENCRYPTION_SALT || "default-encryption-salt").slice(
	0,
	16,
);

/**
 * Encrypt sensitive data (e.g., API keys) using AES-256-GCM
 * @param data - The plaintext data to encrypt
 * @returns Encrypted data as base64 string including IV and auth tag
 */
export function encryptData(data: string): string {
	const key = scryptSync(
		process.env.ENCRYPTION_KEY || "default-key",
		SALT,
		32,
	);
	const iv = randomBytes(16);
	const cipher = createCipheriv(ALGORITHM, key, iv);

	let encrypted = cipher.update(data, "utf8", "hex");
	encrypted += cipher.final("hex");

	const authTag = cipher.getAuthTag();

	// Combine IV + authTag + encrypted data
	const result = iv.toString("hex") + authTag.toString("hex") + encrypted;
	return Buffer.from(result, "hex").toString("base64");
}

/**
 * Decrypt data encrypted with encryptData
 * @param encryptedData - The base64 encoded encrypted data
 * @returns Decrypted plaintext data
 */
export function decryptData(encryptedData: string): string {
	const key = scryptSync(
		process.env.ENCRYPTION_KEY || "default-key",
		SALT,
		32,
	);

	const buffer = Buffer.from(encryptedData, "base64");
	const hex = buffer.toString("hex");

	const iv = Buffer.from(hex.slice(0, 32), "hex");
	const authTag = Buffer.from(hex.slice(32, 64), "hex");
	const encrypted = hex.slice(64);

	const decipher = createDecipheriv(ALGORITHM, key, iv);
	decipher.setAuthTag(authTag);

	let decrypted = decipher.update(encrypted, "hex", "utf8");
	decrypted += decipher.final("utf8");

	return decrypted;
}
